// A concrete {@link BucketPut} that uploads a RASL block straight to Cloudflare R2 via its
// S3-compatible API, signing each request in-browser with AWS Signature V4 (szzt-spec.md
// §13.1, roadmap item 6, transport option "A"). No server sits in the middle: the publisher holds
// R2 credentials and PUTs the object itself, so the "credible exit" property is preserved (the
// gateway still re-hashes every byte on read; the mirror is never trusted).
//
// CID-EXACTNESS FOR FREE. We send the object's payload hash as a *signed* header
// (`x-amz-content-sha256: <sha256hex(bytes)>`, never `UNSIGNED-PAYLOAD`), so R2 verifies
// server-side that the body it received hashes to exactly that value and rejects the PUT otherwise.
// Every block we push is a single-block CIDv1 whose multihash digest IS sha256(bytes) — `raw`
// (0x55) for a resource blob, `dag-cbor` (0x71) for the manifest (§3.2). The object key is the CID
// itself (`.well-known/rasl/<cid>`). So a successful signed PUT proves R2 stored the exact bytes,
// under the exact CID — the same guarantee the PDS-side `BlobCidMismatchError` gives, enforced by
// R2 instead of by us. No ETag/MD5 round-trip is needed.
//
// This runs unchanged in a browser or in modern Node (both provide `fetch` + WebCrypto
// `crypto.subtle`); credentials are read from the config the caller supplies, never logged.
import type { BucketPut } from "./rasl-producer.ts";

/** R2 credentials + bucket the publisher PUTs into (all from the user's Cloudflare dashboard). */
export interface R2Credentials {
  /** Cloudflare account id — forms the S3 endpoint host `<accountId>.r2.cloudflarestorage.com`. */
  accountId: string;
  /** Target bucket name. */
  bucket: string;
  /** R2 API token access key id. */
  accessKeyId: string;
  /** R2 API token secret access key (never logged; lives only in the caller's config). */
  secretAccessKey: string;
}

/** Injectable seams so the signer is unit-testable without a network or a real clock. */
export interface R2PutDeps {
  /** Defaults to the global `fetch`. */
  fetch?: typeof fetch;
  /** Wall clock for the SigV4 `x-amz-date`; defaults to `() => new Date()`. */
  now?: () => Date;
}

const REGION = "auto"; // R2 ignores the region but SigV4 requires one in the credential scope.
const SERVICE = "s3";
const ALGORITHM = "AWS4-HMAC-SHA256";

const HEX = "0123456789abcdef";

function toHex(bytes: Uint8Array): string {
  let out = "";
  for (const b of bytes) out += HEX[b >> 4]! + HEX[b & 0xf]!;
  return out;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return toHex(new Uint8Array(digest));
}

const utf8 = new TextEncoder();

async function hmac(key: Uint8Array, data: string): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, utf8.encode(data));
  return new Uint8Array(sig);
}

/**
 * Percent-encode an S3 object key path per SigV4 canonical-URI rules: every byte except the
 * unreserved set `A-Za-z0-9-_.~` is `%XX`-escaped, and `/` is preserved as a path separator.
 * Our keys (`.well-known/rasl/<base32-cid>`) only contain unreserved chars + `/`, but encode
 * defensively so a future key with an odd char still signs correctly.
 */
function encodeS3Path(key: string): string {
  return key
    .split("/")
    .map((seg) =>
      encodeURIComponent(seg).replace(
        /[!'()*]/g,
        (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase(),
      ),
    )
    .join("/");
}

/** SigV4 basic-date formatting: `YYYYMMDDTHHMMSSZ` (amzDate) and its `YYYYMMDD` prefix (dateStamp). */
function amzDates(now: Date): { amzDate: string; dateStamp: string } {
  const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
  return { amzDate, dateStamp: amzDate.slice(0, 8) };
}

async function signingKey(secret: string, dateStamp: string): Promise<Uint8Array> {
  const kDate = await hmac(utf8.encode("AWS4" + secret), dateStamp);
  const kRegion = await hmac(kDate, REGION);
  const kService = await hmac(kRegion, SERVICE);
  return hmac(kService, "aws4_request");
}

/** Thrown when R2 rejects the upload (auth, content-hash mismatch, CORS-permitted error, quota…). */
export class R2PutError extends Error {
  constructor(
    readonly key: string,
    readonly status: number,
    readonly body: string,
  ) {
    super(`R2 PUT ${key} failed: HTTP ${status}${body ? ` — ${body.slice(0, 300)}` : ""}`);
    this.name = "R2PutError";
  }
}

/**
 * Build a {@link BucketPut} that stores each block in Cloudflare R2 under its RASL object key,
 * signing the PUT with SigV4 from the supplied {@link R2Credentials}. Pair with
 * `raslBucketProducer({ host, put })` where `host` is the bucket's public serve domain (an R2
 * custom domain that maps `/.well-known/rasl/<cid>` to the object). Rejects with {@link R2PutError}
 * on any non-2xx response, which the producer/publish path treats as a failed push (no hint written).
 *
 * NOTE (browser): the R2 bucket must have a CORS policy allowing `PUT` from the publisher's origin,
 * and the API token must be scoped to Object Read & Write on the bucket.
 */
export function r2BucketPut(creds: R2Credentials, deps: R2PutDeps = {}): BucketPut {
  const doFetch = deps.fetch ?? fetch;
  const now = deps.now ?? (() => new Date());
  const host = `${creds.accountId}.r2.cloudflarestorage.com`;

  return async (key: string, bytes: Uint8Array, contentType: string): Promise<void> => {
    const { amzDate, dateStamp } = amzDates(now());
    const canonicalUri = `/${encodeS3Path(creds.bucket)}/${encodeS3Path(key)}`;
    const payloadHash = await sha256Hex(bytes);

    // Signed headers, sorted by lowercased name (SigV4 requires alphabetical order).
    const headers: Record<string, string> = {
      "content-type": contentType,
      host,
      "x-amz-content-sha256": payloadHash,
      "x-amz-date": amzDate,
    };
    const signedHeaders = Object.keys(headers).sort().join(";");
    const canonicalHeaders = Object.keys(headers)
      .sort()
      .map((h) => `${h}:${headers[h]!.trim()}\n`)
      .join("");

    const canonicalRequest = [
      "PUT",
      canonicalUri,
      "", // no query string
      canonicalHeaders,
      signedHeaders,
      payloadHash,
    ].join("\n");

    const credentialScope = `${dateStamp}/${REGION}/${SERVICE}/aws4_request`;
    const stringToSign = [
      ALGORITHM,
      amzDate,
      credentialScope,
      await sha256Hex(utf8.encode(canonicalRequest)),
    ].join("\n");

    const signature = toHex(await hmac(await signingKey(creds.secretAccessKey, dateStamp), stringToSign));
    const authorization =
      `${ALGORITHM} Credential=${creds.accessKeyId}/${credentialScope}, ` +
      `SignedHeaders=${signedHeaders}, Signature=${signature}`;

    const res = await doFetch(`https://${host}${canonicalUri}`, {
      method: "PUT",
      // `host` is set by the agent; the rest are the signed headers plus the Authorization.
      headers: {
        "content-type": contentType,
        "x-amz-content-sha256": payloadHash,
        "x-amz-date": amzDate,
        authorization,
      },
      body: bytes,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new R2PutError(key, res.status, body);
    }
  };
}
